import InstanceFactory from "../../src/factories/InstanceFactory";
import GameModel from "../../src/model/GameModel";
import SoundVars from "../const/SoundVars";
import { DefaultLayers } from "../utils/Layers";
import SoundController from "../utils/SoundController";
import ViewManager from "../utils/ViewManager";
import BaseView from "./BaseView";
import GameEvents from "../../src/const/GameEvents";
import WinlinesView from "./WinlinesView";
import WinSymbolsView from "./WinSymbolsView";
import BaseAnticipation from "./BaseAnticipation";
import WinValue from "./WinValue";
import Reel from "./Reel";
import ReelSymbol from "./Symbol";
import { TweenMax, Tween, TimelineMax } from "gsap";
import App from "../App";
import TextField from "./components/TextField";
import betModel from "../../src/model/BetModel";
import SpinModel from "../../src/model/SpinModel";
import { IWinData, ISpinificationData } from "../../src/server/data/ISpinData";
import Layout from "../utils/Layout";
import SceneView from "./SceneView";
import AbstractReelBehaviour from "../spinners/AbstractReelBehaviour";
import SymbolAnimation from "./SymbolAnimation";
import Utils from "../utils/Utils";
import ConsoleUtil from "../utils/ConsoleUtil";

/**
 * A view which is a group of all the reels
 */
class ReelGroup extends BaseView {
    // public bg: PIXI.Graphics;
    public reels: Reel[];
    public spinners: AbstractReelBehaviour[];
    public allResultsShown: boolean;
    public winlinesView: WinlinesView;
    public winSymbolsView: WinSymbolsView;
    public anticipation: BaseAnticipation;
    public hasAnticipation: boolean;
    public anticipationStarted: boolean;
    public winSound: Howl;
    protected winFrameContainer: PIXI.Container;
    protected symContainer: PIXI.Container;
    protected winInfoLabelBg: PIXI.Sprite;
    protected winInfoLabelTF: TextField;
    protected spinDelayedCalls: Tween[];
    protected reelsSpinning: number;
    protected reelStopping: number;
    protected isMinSpinTimeComplete: boolean;
    protected stopSymbolsAvailable: boolean;
    protected stoppingStarted: boolean;
    protected stopDelayedCalls: Tween[];
    protected spinificationData?: ISpinificationData;
    // protected stopImmediatelyFlag: boolean;
    protected winLineTimeline: TimelineMax;
    private symAnimCount: number = 0;
    private winGroupToAnim: number = 0;
    private stopDelay: number = 0;
    protected resultData: any[][];
    protected groupConfig: {
        winLineLayer?: string;
        celеbrationLayer?: string;
        winSymbolsLayer?: string;
        reelExtraSymbols?: number;
    };
    private symBounds;
    private spinTime: number;

    constructor() {
        super();
    }

    public create(
        scene: SceneView,
        symbolData?: any[][],
        groupConfig?: {
            winLineLayer?: string;
            celеbrationLayer?: string;
            winSymbolsLayer?: string;
            reelExtraSymbols?: number;
        },
        forceLandscapeConfig:boolean = true
    ) {
        this.groupConfig = groupConfig || {};
        this.groupConfig.reelExtraSymbols = isNaN(
            this.groupConfig.reelExtraSymbols
        )
            ? this.config.reelExtraSymbols || 0
            : this.groupConfig.reelExtraSymbols;

        if (forceLandscapeConfig) {
            scene.forceLandscape();
        }
        this.parceSceneBounds(scene);
        const positionDefinitions = scene.getPositionDefinitions();

        const symBounds = positionDefinitions.get("sym");
        this.symBounds = symBounds;
        const reelSeparator = positionDefinitions.get("reel_border");
        const rowSeparator = positionDefinitions.get("row_border");

        this.winlinesView = InstanceFactory.getClass("WinlinesView");
        this.winSymbolsView = InstanceFactory.getClass("WinSymbolsView");
        this.anticipation = InstanceFactory.getClass("Anticipation");
        this.winSymbolsView.on(
            GameEvents.SHOW_SYMBOL_AT,
            this.showSymbolAt.bind(this)
        );
        this.winSymbolsView.on(
            GameEvents.HIDE_SYMBOL_AT,
            this.hideSymbolAt.bind(this)
        );

        this.reels = [];
        this.spinners = [];

        this.symContainer = new PIXI.Container();
        this.addChild(this.symContainer);
        let sceneBounds =
            scene.sceneBounds || scene.containers.get("reels").getBoundConfig();
        scene.sceneBounds = sceneBounds;
        // const numReels = Math.floor(sceneBounds.width / symBounds.width);
        const numReels = this.calculateReelsCount(scene);
        const numSyms = Math.floor(sceneBounds.height / symBounds.height);
        this.winlinesView.create(
            scene,
            this.groupConfig.winLineLayer,
            this.groupConfig.celеbrationLayer,
            numSyms
        );
        const symPositionsConfig = [];
        for (let reelIdx = 0; reelIdx < numReels; reelIdx++) {
            const reelBounds = positionDefinitions.get("reel_" + reelIdx);
            const reelData = symbolData
                ? symbolData[reelIdx] || SpinModel.getStopSymsForReel(reelIdx)
                : SpinModel.getStopSymsForReel(reelIdx);
            const r = this.gameFactory.getClass("Reel", {
                container: this.symContainer,
                reelIdx,
            }) as Reel;
            r.create(
                reelData,
                {
                    symBounds,
                    rowSeparator,
                    reelBounds,
                    reelSeparator,
                    extraSymbols: this.groupConfig.reelExtraSymbols,
                    sceneName: scene.name,
                },
                positionDefinitions
            );
            this.reels.push(r);
            const spinner = this.gameFactory.getClass("SpinBehaviour", {
                reel: r,
                sortFunction: this.sortSymbols.bind(this),
            });
            this.spinners.push(spinner);
        }

        this.sortSymbols();
        this.winSymbolsView.create(
            scene,
            this.groupConfig.reelExtraSymbols,
            this.groupConfig.winSymbolsLayer
        );

        // this.addEventListener(this.dispatcher, GameEvents.START_SPIN, this.spin, this);
        // this.addEventListener(this.dispatcher, GameEvents.FINISH_SPIN, this.stop, this);
        // this.addEventListener(this.dispatcher, GameEvents.STOP_SPIN, this.stopImmediately, this);
        scene.onOrientationChange();
    }

    public getCurrentStoppingReel() {
        return this.reelStopping;
    }

    public setAnticipation(anticipation:BaseAnticipation) {
        this.anticipation = anticipation;
    }

    public setSpinner(spinner: AbstractReelBehaviour, reelId: number) {
        this.spinners[reelId] = spinner;
    }

    public maskReels(mask?: PIXI.Graphics ){
        this.reels.forEach( r => {
            r.maskReel(mask)
        })
    }

    calculateReelsCount(scene): number {
        let count = 0;
        let reel = scene.positions.get("reel_" + count);
        while (reel) {
            count++;
            reel = scene.positions.get("reel_" + count);
        }
        return count;
    }

    parceSceneBounds(scene) {
        if (
            scene.containers &&
            scene.containers.has("reels") &&
            scene.positions
        ) {
            scene.positions.forEach((element) => {
                element.x =
                    element.x - Layout.getX(scene.containers.get("reels"));
                element.y =
                    element.y - Layout.getY(scene.containers.get("reels"));
            });
        }
    }

    public hideLinesAndInfo(hideSymbols = false) {
        if (hideSymbols) {
            this.stopSymbolAnimations();
        }
        if (this.winSound) {
            SoundController.stopSound(this.winSound);
        }
        this.winlinesView.hideLines();
        this.clearWinInfoLabel();
    }

    public stopSymbolAnimations() {
        this.off(GameEvents.GROUP_ANIM_COMPLETE);
        for (let i = 0; i < this.reels.length; i++) {
            const reel = this.reels[i];
            const numSyms = reel.symbols.length;
            for (let symIdx = 0; symIdx < numSyms; symIdx++) {
                reel.symbols[symIdx].stopAnimation();
            }
        }
        this.winSymbolsView.stopAll();
    }

    public stopLinesLoop() {
        this.off(GameEvents.GROUP_ANIM_COMPLETE);
    }


    public sortSymbols() {
        if (this.config.symDepthSortOrderById) {
            this.symContainer.children.sort(this.symCompare.bind(this));
            this.winSymbolsView.children.sort(this.symCompare.bind(this));
        }
    }

    public symCompare(s1: PIXI.DisplayObject, s2: PIXI.DisplayObject) {
        const id1 = s1 instanceof ReelSymbol ? s1.getId() : -1;
        const id2 = s2 instanceof ReelSymbol ? s2.getId() : -1;

        let sortRes = 0;

        const orderIdx1 = this.config.symDepthSortOrderById.indexOf(id1);
        const orderIdx2 = this.config.symDepthSortOrderById.indexOf(id2);

        const z1 = s1 instanceof ReelSymbol && s1.zOrder ? s1.zOrder : 0;
        const z2 = s2 instanceof ReelSymbol && s2.zOrder ? s2.zOrder : 0;

        // if (z1 > z2) {
        //     sortRes = 1;
        // } else if (z1 < z2) {
        //     sortRes = -1;
        // } else
        if (orderIdx1 < orderIdx2) {
            sortRes = 1;
        } else if (orderIdx1 > orderIdx2) {
            sortRes = -1;
        } else {
            sortRes = Utils.sortSymbols(s1, s2, this.config.symPosOrder || {});
        }

        return sortRes;
    }

    public spin(delay: number = 0,easeFunc?:( number )=>number) {

        this.spinTime = new Date().getTime();

        if (this.winLineTimeline) {
            this.winLineTimeline.kill();
        }
        this.hasAnticipation = false;
        this.anticipationStarted = false;
        this.spinificationData = null;
        this.reelStopping = 0;
        // const heldReels = spinModel.getPlayerHoldsPattern();
        if(GameModel.turboSpin) {
            delay = 0;
        }
        SoundController.playSound(SoundVars.EFFECT_REEL_START);
        this.spinDelayedCalls = [];
        this.reelsSpinning = 0;
        let reelsToSpin = 0;
        // let delay = 0;
        for (let reelIdx = 0; reelIdx < this.spinners.length; reelIdx++) {
            const s = this.spinners[reelIdx];
            const r = this.reels[reelIdx];
            // r.isHeld = heldReels[reelIdx];
            if (!r.isHeld) {
                let currentDelay = delay * reelsToSpin;
                reelsToSpin++;
                // delay += reelIdx * .075;
                this.spinDelayedCalls.push(
                    this.addDelayedCall(currentDelay, () => { 
                        this.reelsSpinning++;
                        s.spin(easeFunc);
                        if( this.reelsSpinning === this.spinners.length - 1) {
                            this.emit(GameEvents.ALL_REELS_STARTED);
                        }
                    }, [], s)
                );
            }
        }

        this.isMinSpinTimeComplete = false;
        this.stoppingStarted = false;
        this.stopSymbolsAvailable = false;
        this.stopDelayedCalls = [];
        SpinModel.stopImmediately = false;

        ConsoleUtil.yellow("MIN TIME: " + this.config.minSpinTime);
        ConsoleUtil.yellow("DELAY: " + delay);
        let reelDelay = Math.max(delay * reelsToSpin, this.config.minSpinTime);
        if (GameModel.turboSpin) {
            reelDelay = Math.max(delay * reelsToSpin, this.config.quickSpinTime);
        }


        this.addDelayedCall(reelDelay, () => this.allowStop(), [], this);

        // TODO SOME SORT OF STATE MACHINE
        // if (this.controller.getState() == C_GameState.START_SPIN_1)
        // {
        // this.dispatcher.emit(GameEvents.START_SPIN_1_SEQUENCE_SHOWN);
        // }
        // else
        // {
        //     this.dispatcher.emit(GameEvents.START_SPIN_2_SEQUENCE_SHOWN);
        // }
    }

    public isReelHeld(reelIdx: number):boolean {
        return  this.reels[reelIdx].isHeld;
    }

    public holdReel(reelIdx: number) {
        this.reels[reelIdx].isHeld = true;
    }

    public releaseReel(reelIdx: number) {
        this.reels[reelIdx].isHeld = false;
    }

    public countHeldReels():number {
        let count = 0;
        this.reels.forEach((reel) => {
            if(reel.isHeld) {
                count++;
            }
        });

        return count;
    }

    public releaseAllReels() {
        this.reels.forEach((reel) => {
            reel.isHeld = false;
        });
    }

    public allowStop() {
        this.isMinSpinTimeComplete = true;
        if (this.stopSymbolsAvailable) {
            this.stopReels(this.stopDelay);
        }
    }

    // chainSpin(delay = 0){
    //     if (this.stopSymbolsAvailable) {

    //     }
    // }

    public stopImmediately(stopData: any[][]) {
        this.resultData = stopData;
        TweenMax.killDelayedCallsTo(this.sound.playMusic);
        // this.sound.restoreMusic(); //Crashes game when during freespins, spinning is stopped 2 times in a row
        SpinModel.stopImmediately = true;
        for (let reelIdx = 0; reelIdx < this.spinners.length; reelIdx++) {
            const s = this.spinners[reelIdx];
            TweenMax.killDelayedCallsTo(s.spin);
            s.activateQuickStop();
        }

        if (this.stoppingStarted) {
            for (
                let delayedCallIdx = 0;
                delayedCallIdx < this.stopDelayedCalls.length;
                delayedCallIdx++
            ) {
                const delayedCall = this.stopDelayedCalls[delayedCallIdx];
                if (delayedCall != null) {
                    delayedCall.progress(1);
                }
            }
        } else {
            console.log("stopImmediately()");
            this.stopReels();
        }
    }

    public stop(stopData: any[][], delay = 0, spinificationData?: ISpinificationData) {
        this.stopSymbolsAvailable = true;
        this.resultData = stopData;
        this.spinificationData = spinificationData;
        this.stopDelay = delay;
        if (this.isMinSpinTimeComplete) {
            console.log("stop()");
            this.stopReels(delay);
        }
    }

    protected stopReels(delay = 0) {
        if (!this.stoppingStarted) {
            this.stoppingStarted = true;
            this.spinDelayedCalls.forEach((delayedCall) => delayedCall.kill());
            this.reelStopping = 0;
            // let delay = 0;
            let spinificationData = this.spinificationData || SpinModel.spinification;

            for (let reelIdx = 0; reelIdx < this.reels.length; reelIdx++) {
                const r = this.reels[reelIdx];
                const s = this.spinners[reelIdx];

                if (!r.isHeld) {
                    // In case stop starts before the delayed spin
                    s.spin();
                }

                r.once(
                    GameEvents.REEL_STOPPED,
                    () => this.onReelStopped(reelIdx,spinificationData),
                    this
                );
                if (SpinModel.stopImmediately) {
                    delay += App.config.instantStopDelayTime;
                } else if (GameModel.turboSpin) {
                    delay += App.config.quickStopDelayTime;
                } else if (
                    spinificationData &&
                    spinificationData.spinificationIndex <= reelIdx &&
                    spinificationData.spinificationEndIndex > 0 &&
                    spinificationData.spinificationIndex <= spinificationData.spinificationEndIndex &&
                    (
                        spinificationData.spinificationEndIndex >= reelIdx ||
                        this.hasAnticipation
                    )
                ) {
                    this.hasAnticipation = true;
                    // let spinificationStartIndex = spinificationData.spinificationIndices? spinificationData.spinificationIndices[reelIdx] : reelIdx;
                    if (reelIdx === 0 && spinificationData.spinificationEndIndex > reelIdx) {
                        // s.activateAnticipation();
                        // this.anticipation.play(reelIdx);
                        this.startAnticipation(reelIdx, spinificationData);
                    }
                    // s.activateAnticipation();
                    // delay += (spinificationStartIndex + 1) * App.config.reelSpinificationDuration;
                } else if (!r.isHeld) {
                    delay += App.config.reelStopDelayTime;
                }

                if (!this.hasAnticipation) {
                    this.stopDelayedCalls.push(
                        this.addDelayedCall(
                            delay,
                            s.stop,
                            [this.resultData[reelIdx]],
                            s
                        )
                    );
                }

                // this.stopDelayedCalls.push(this.addDelayedCall(delay , s.stop, [SpinModel.getStopSymsForReel(reelIdx)], s));
            }
        }
    }

    public onReelStopped(reelIdx: number, spinificationData: ISpinificationData) {
        this.reelsSpinning--;
        let nextreelIdx = reelIdx + 1;
        this.reelStopping = nextreelIdx;

        if (
            this.hasAnticipation &&
            spinificationData &&
            spinificationData.spinificationIndex <= nextreelIdx &&
            spinificationData.spinificationEndIndex > 0 &&
            spinificationData.spinificationIndex <= spinificationData.spinificationEndIndex &&
            (
                spinificationData.spinificationEndIndex >= nextreelIdx
                || this.anticipationStarted
            )
        ) {
            
            // const s = this.spinners[nextreelIdx];
            // s.activateAnticipation();
            // this.anticipation.play(nextreelIdx);
            this.startAnticipation(nextreelIdx, spinificationData);
            // this.reels[nextreelIdx].spinnify();
        }
        this.anticipation.stop(this.reelsSpinning, reelIdx );
        if (this.reelsSpinning == 0) {
            this.dispatcher.emit(GameEvents.ALL_REELS_STOPPED);
            this.emit(GameEvents.ALL_REELS_STOPPED);

            let stopTime = new Date().getTime();

            ConsoleUtil.yellow("[ReelGroup] SPIN DURATION: " + ((stopTime - this.spinTime) / 1000).toFixed(2));
        }
    }
    
    public startAnticipation(reelId: number, spinificationData: ISpinificationData = this.spinificationData ) {
        const s = this.spinners[reelId];
        if (s) {
            this.anticipationStarted = true;
            let isAnticipatingReel =  spinificationData.spinificationIndex <= reelId && spinificationData.spinificationEndIndex >= reelId;
            if (isAnticipatingReel) {
                s.activateAnticipation();
                this.anticipation.play(reelId);
            } else {
                this.anticipation.stop(0, reelId );
            }
            let delay =  isAnticipatingReel ?
            App.config.reelSpinificationDuration :
            0;
            let stopTime = isAnticipatingReel ?
            App.config.anticipationStopTime :
            App.config.afterAnticipationReelStopTime;
            this.stopDelayedCalls.push(
                this.addDelayedCall(
                    delay ,
                    s.stop,
                    [this.resultData[reelId], isAnticipatingReel, stopTime],
                    s
                )
            );
        }
    }

    public displaySummaryResult(winlinesToShow: IWinData[]) {
        if (GameModel.gameConfig.waysGame) {
            //** WAYS WIN **/
            this.showWaysSummary(winlinesToShow);
        } else {
            //** WIN LINES WIN **/
            this.showSummaryWinLines(winlinesToShow);
        }
    }

    public showFinalSymbols(finalSyms: any[][]) {
        for (let reelIdx = 0; reelIdx < this.reels.length; reelIdx++) {
            // let result = SpinModel.getFinalSymsForReel(reelIdx);
            let result = finalSyms[reelIdx];
            // TODO handling for more above and below symbols
            for (
                let symId = this.groupConfig.reelExtraSymbols;
                symId <
                this.reels[reelIdx].symbols.length -
                    this.groupConfig.reelExtraSymbols;
                symId++
            ) {
                this.reels[reelIdx].symbols[symId].setId(
                    result[symId - this.groupConfig.reelExtraSymbols]
                );
            }
        }
    }

    public displaySpinResults(winlinesToShow: IWinData[], repeat?: number,showBaners = false) {
        if (!winlinesToShow || !winlinesToShow.length) {
            return;
        }
        if (GameModel.gameConfig.waysGame) {
            this.showWaysGroups(winlinesToShow,showBaners);
        } else {
            this.showWinLines(winlinesToShow, repeat,showBaners);
        }
    }

    public showWaysGroups(winsData,showBanner = false) {
        this.winGroupToAnim = 0;
        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            this.winGroupToAnim += 1;
            if (this.winGroupToAnim > winsData.length - 1) {
                this.winGroupToAnim = 0;
            }
            this.animateWaysGroup(winsData[this.winGroupToAnim].coordinates);
            if(showBanner) {
                this.winlinesView.setWinBannerValue(
                    winsData[this.winGroupToAnim].creditsWon
                    );
            }
        });
        this.animateWaysGroup(winsData[this.winGroupToAnim].coordinates);
        if(showBanner) {
            this.winlinesView.setWinBannerValue(
                winsData[this.winGroupToAnim].creditsWon
                );
        }
    }

    public animateWaysGroup(group: { x: number; y: number }[]) {
        // let symbol;
        let counter = group.length;
        for (let i = 0; i < group.length; i++) {
            let symbol = this.getSymbolAt(group[i].x - 1, group[i].y - 1);
            symbol.showWin();
            this.winSymbolsView
                .playAnimaAtReelCell(
                    group[i].x - 1,
                    group[i].y - 1,
                    symbol.getId(),
                    "anim"
                )
                .then(() => {
                    counter--;
                    if (counter <= 0) {
                        this.emit(GameEvents.GROUP_ANIM_COMPLETE);
                    }
                });

            // winAnim.showWin(symbol.getId(), false).then(() => {
            //     counter--;
            //     if (counter <= 0) {
            //         console.log("symbol anim complete");
            //         this.emit(GameEvents.GROUP_ANIM_COMPLETE);
            //     }
            // })
        }
    }

    public showWaysSummary(windata: IWinData[]) {
        // TODO use the windata insteand of winningSymbols
        const winningSymbols = SpinModel.getWinningSymbols();
        this.symAnimCount = 0;
        for (let i = 0; i < winningSymbols.length; i++) {
            for (let j = 0; j < winningSymbols[i].length; j++) {
                if (winningSymbols[i][j]) {
                    const symbol = this.getSymbolAt(i, j);
                    symbol.showWin();
                    this.symAnimCount++;

                    this.winSymbolsView
                        .playAnimaAtReelCell(i, j, symbol.getId(), "anim")
                        .then(() => {
                            this.symAnimCount--;
                            if (this.symAnimCount == 0) {
                                this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
                                console.log(GameEvents.WIN_ANIMATIONS_COMPLETE);
                            }
                        });
                }else {
                    const symbol = this.getSymbolAt(i, j);
                    symbol.blackening()
                }
            }
        }
        console.log(windata, "www")
        for (let k = 0; k < windata.length; k++) {
            let x = windata[k].coordinates[0].x + 1
            let y = windata[k].coordinates[0].y + 1
            let coordinate = this.getSymbolAt(x, y)
            this.winlinesView.setGroupWinBannerValue(
                windata[k].creditsWon,
                App.config.bangupWin,
                coordinate.x,
                coordinate.y
            );
            console.log("tryy", coordinate)
            // if(k == windata.length - 1){
            //     // this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
            // }
        }
    }

    public showSummaryWinLines(winlinesToShow: IWinData[]) {
        this.winSound = SoundController.playSound(
            SoundVars[SpinModel.getWinsoundName()]
        );
        this.off(GameEvents.GROUP_ANIM_COMPLETE);

        let repeatCount = GameModel.gameConfig.symAnimSummaryRepeat || 1;
        
        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            if (--repeatCount <= 0) {
                winlinesToShow.forEach((winline) => this.hideWinlineWin(winline));
                this.finishShowingWinlines();
            } else {
                this.playAnimsOnce(winlinesToShow);
            }
        });
        this.playAnimsOnce(winlinesToShow);
    }

    private playAnimsOnce(winlinesToShow: IWinData[]) {
        winlinesToShow.forEach((winline) => {
            this.highlightWinlineWin(winline, false);
            this.winlinesView.highlightWinLine(winline, false);
        })
    }

    public showWinLines(winlinesToShow: IWinData[], repeat = -1,showBaners = false) {
        this.winGroupToAnim = 0;
        let competed = false;
        this.off(GameEvents.GROUP_ANIM_COMPLETE);
        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            let lastId = this.winGroupToAnim;
            this.winGroupToAnim += 1;
            if (this.winGroupToAnim > winlinesToShow.length - 1) {
                this.winGroupToAnim = 0;
                this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
                competed = true;
                if ( repeat > 0) {
                    repeat--;
                }

                if (repeat === 0) {
                    this.off(GameEvents.GROUP_ANIM_COMPLETE);

                }
            }
            if (!competed || repeat !== 0) {
                this.hideWinlineWin(winlinesToShow[lastId]);
                const winline = winlinesToShow[this.winGroupToAnim];
                this.highlightWinlineWin(winline, showBaners);
                this.winlinesView.highlightWinLine(winline);
            }
        });
        const winlineData = winlinesToShow[this.winGroupToAnim];
        this.highlightWinlineWin(winlineData, showBaners);
        this.winlinesView.highlightWinLine(winlineData);
    }

    public showTotalWinnings() {
        // TODOCHANGE THIS Add INTERFACE
        // if (this.gameFactory instanceof CustomGameFactory) {
        //     const totalWinningsPopup = this.gameFactory.getClass("TotalWinningsPopup");
        //     totalWinningsPopup.create();
        //     this.parent.addChild(totalWinningsPopup);
        // }
    }

    public highlightWinlineWin(winlineData: IWinData, showWinValue: boolean) {
        const winSyms = this.getWinlineWinSymbols(winlineData);
        // const winAnims = this.winSymbolsView.getWinlineWinSymbols(winlineData);
        let ids = [];
        let counter = winSyms.length;
        for (let symIdx = 0; symIdx < winSyms.length; symIdx++) {
            const symbol: ReelSymbol = winSyms[symIdx];
            // const winAnim: SymbolAnimation = winAnims[symIdx];
            if (symbol.getReelIdx() == 2 && showWinValue) {
                let index;
                index =
                    1 +
                    Math.floor(
                        symbol.y / Layout.scalePixels(this.symBounds.height)
                    );

                this.winlinesView.showLineValue(index, winlineData.creditsWon);
            }
            symbol.showWin(true);
            ids.push(symbol.getId());
            // winAnim.showWin(symbol.getId(), true).then(() => {
            //     counter--;
            //     if (counter <= 0) {
            //         this.emit(GameEvents.GROUP_ANIM_COMPLETE);

            //     }
            // });
        }

        this.winSymbolsView.playWinLineSymbols(winlineData, ids).then(() => {
            this.emit(GameEvents.GROUP_ANIM_COMPLETE);
        });
        // if (counter <= 0) {

        //     this.emit(GameEvents.GROUP_ANIM_COMPLETE);
        // }
    }

    public hideWinlineWin(winlineData: IWinData) {
        let winlineWinSymbols = this.getWinlineWinSymbols(winlineData);
        winlineWinSymbols.forEach((symbol) => {
            if (!SpinModel.bonusWin) {
                symbol.hideWin();
            } else {
                if (
                    symbol.reelIdx >= SpinModel.firstWildIndex &&
                    symbol.reelIdx <
                        SpinModel.firstWildIndex + SpinModel.wildVersion - 1
                ) {
                    if (
                        this.reels[symbol.reelIdx].symbols.indexOf(symbol) == 0
                    ) {
                        symbol.hideWin();
                    }
                } else {
                    symbol.hideWin();
                }
            }
        });
        this.winSymbolsView.hideWinlineWinSymbols(winlineData);
    }

    public finishShowingWinlines() {
        // this.dispatcher.emit(GameEvents.SHOW_WIN_LINES_COMPLETE);
        this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
    }

    public getWinlineWinSymbols(winLineData: IWinData): ReelSymbol[] {
        const winningReels = winLineData.winReels;
        const winningPositions = winLineData.winSymbols;

        const winSymbols: ReelSymbol[] = [];

        for (
            let winReelIdx = 0;
            winReelIdx < winningReels.length;
            winReelIdx++
        ) {
            const reelIdx = winningReels[winReelIdx];
            const symIdx =
                winningPositions[winReelIdx] +
                this.groupConfig.reelExtraSymbols; // Win positions start from 1

            const reel = this.reels[reelIdx];
            const sym = reel.symbols[symIdx];

            winSymbols.push(sym);
        }

        return winSymbols;
    }

    public clearWinInfoLabel() {
        // this.winlinesView.winValueBanner.visible = false;
        if (this.winInfoLabelBg) {
            TweenMax.to(this.winInfoLabelBg, 0.1, {
                alpha: 0,
                onComplete: () => {
                    this.parent.removeChild(this.winInfoLabelBg);
                    this.winInfoLabelBg = null;
                },
                onCompleteScope: this,
            });
        }

        if (this.winInfoLabelTF) {
            TweenMax.to(this.winInfoLabelTF, 0.1, {
                alpha: 0,
                onComplete: () => {
                    this.parent.removeChild(this.winInfoLabelTF);
                    this.winInfoLabelTF = null;
                },
                onCompleteScope: this,
            });
        }
    }

    /**
     *
     * @param data {IWinData}
     */
    public createWinInfoLabel(data: IWinData) {
        let scale = false;
        if (!this.winInfoLabelBg) {
            this.winInfoLabelBg = this.assets.getSprite("winInfoLabelBg");
            this.winInfoLabelBg.anchor.set(0.5, 0.5);
            this.winInfoLabelBg.width = Layout.relWidth(
                this.config.winInfoLabelRect.width
            );
            this.winInfoLabelBg.height = Layout.relHeight(
                this.config.winInfoLabelRect.height
            );
            Layout.horizontalAlignCenter(
                this.winInfoLabelBg,
                this.config.winInfoLabelRect.x
            );
            Layout.verticalAlignMiddle(
                this.winInfoLabelBg,
                this.config.winInfoLabelRect.y
            );
            this.parent.addChild(this.winInfoLabelBg);
            scale = true;
        }

        let text;
        if (data.lineId >= 0) {
            text =
                this.locale.getString("flash_dynamibox_line") +
                " " +
                (data.lineId + 1) +
                "\n";
            text =
                this.locale.getString("gui_dynamic_line") +
                " " +
                (data.lineId + 1) +
                "\n";
        } else if (data.winSymId === this.config.bonusSymId) {
            text = this.locale.getString("flash_dynamicbox_line_bous") + "\n";
            text = this.locale.getString("gui_dynamic_bonus") + "\n";
        } else {
            text = this.locale.getString("flash_dynamicbox_line_scater") + "\n";
            text = this.locale.getString("gui_dynamic_scatter") + "\n";
        }

        if (data.creditsWon > 0) {
            text += betModel.formatCurrency(data.creditsWon);
        } else {
            text += this.locale.getString("gui_lineselector_win");
        }

        if (!this.winInfoLabelTF) {
            this.winInfoLabelTF = this.assets.getTF(
                text,
                this.winInfoLabelBg.height * 0.35
            );
            this.winInfoLabelTF.anchor.set(0.5, 0.5);
            this.winInfoLabelTF.x = this.winInfoLabelBg.x;
            this.winInfoLabelTF.y = this.winInfoLabelBg.y;
            this.parent.addChild(this.winInfoLabelTF);
            scale = true;
        } else {
            this.winInfoLabelTF.text = text;
        }

        if (scale) {
            TweenMax.from(this.winInfoLabelTF.scale, 0.2, { x: 0, y: 0 });
            TweenMax.from(this.winInfoLabelBg.scale, 0.2, { x: 0, y: 0 }); // ??????
        }
    }

    public getSymbolAt(reelId: number, rowId: number): ReelSymbol {
        return this.reels[reelId].symbols[
            rowId + this.groupConfig.reelExtraSymbols
        ];
    }

    public setSymbolAt(reelId: number, rowId: number, symId:number) {
        this.reels[reelId].symbols[
            rowId + this.groupConfig.reelExtraSymbols
        ].setId(symId)
    }

    public revealAllSymbols() {
        this.reels.forEach((reel) => {
            reel.symbols.forEach((symbol) => {
                symbol.forceHidden = false;
                symbol.show();
            });
        });
    }

    public showSymbolAt(position: {
        reelId: number;
        row: number;
        id?: number;
    }) {
        let sym = this.getSymbolAt(position.reelId, position.row);
        if(sym) {
            sym.show();
        } else {
            ConsoleUtil.red("Trying to show missing symbol at:" + position.reelId + "x" + position.row);
        }
        if (!isNaN(position.id)) {
            sym.setId(position.id);
        }
    }

    public hideSymbolAt(position: { reelId: number; row: number }) {
        let sym = this.getSymbolAt(position.reelId, position.row);
        if(sym) {
            if(this.spinners[position.reelId].isSettled()) {
                sym.hide();
            } else {
                this.reels[position.reelId].once(GameEvents.REEL_STOPPED,()=>{
                    sym = this.getSymbolAt(position.reelId, position.row);
                    sym.hide();
                });
            }
        } else {
            ConsoleUtil.red("Trying to hide missing symbol at:" + position.reelId + "x" + position.row);
        }
    }

    destroy() {
        this.winlinesView.destroy();
        this.winSymbolsView.destroy();
        super.destroy();
    }
}

export default ReelGroup;
